import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import cx from 'classnames';
import _ from 'lodash';
import {Icon, Popover} from '@perion-undertone/ut-react-common';

import {setFilterOptions} from './../actions/gridCustomizationActions';

const CLASS_NAMES = {
    CONTAINER: 'filter-icon',
    ICON: 'icon-filter'
};

// Push `allFilterOptions` into the redux store so consumers (e.g. the grid's
// "Used Filters" tooltip) can resolve filter aliases back to display names.
//
// Historically this dispatch lived in `render()`, which was effectively
// `dispatch-on-every-render`. It mostly went unnoticed because the v6
// `react-data-grid` memoized header cells aggressively. v7 re-renders
// headers on every grid render, which turned the legacy bug into an
// infinite update loop ("Cannot update a component (`AppLayout`) while
// rendering a different component (`FilterIcon`)" -> repeated
// `SET_FILTER_OPTIONS`). We now only dispatch when the structural value
// of `allFilterOptions` actually changes.
class FilterIcon extends Component {
    state = {
        isOpen: false
    };

    componentDidMount() {
        this.syncFilterOptions();
    }

    componentDidUpdate(prevProps) {
        if (!_.isEqual(prevProps.allFilterOptions, this.props.allFilterOptions)) {
            this.syncFilterOptions();
        }
    }

    syncFilterOptions() {
        const {allFilterOptions, setFilterOptions} = this.props;
        if (allFilterOptions) {
            setFilterOptions(allFilterOptions);
        }
    }

    renderIcon() {
        return <div className={cx(CLASS_NAMES.CONTAINER)}><Icon icon={CLASS_NAMES.ICON} hookId="popover-filter-icon" /></div>;
    }

    // Two interlocking quirks force the controlled-popover pattern below:
    //
    //   1. `ut-react-common`'s `<Popover>` is broken in uncontrolled mode.
    //      It spreads floating-ui's `getReferenceProps()` (which includes
    //      `onClick: toggleHandler`) onto the trigger, then declares its
    //      own `onClick: handleTriggerClick` *after* the spread. The
    //      explicit key wins, so the toggle handler is silently dropped
    //      and clicking the trigger does nothing. Other call sites in
    //      this codebase (e.g. `PeriodDropdownSelect`) work around it by
    //      running the popover in controlled mode (`open={...}` +
    //      explicit `onClick`).
    //
    //   2. v7's `<HeaderCell>` attaches `onMouseDown` (selectCell) and
    //      `onClick` (sort, when sortable) to the column header div. The
    //      filter icon lives inside that header, so without intercepting
    //      we get a race: clicking the icon also fires v7's header
    //      handlers (selecting the cell, sometimes triggering a re-render
    //      that closes the popover). Stopping propagation at an inline
    //      wrapper keeps v7 out of the way without disturbing the
    //      `.hoverable-filter-icon { display: inline; opacity: 0 }`
    //      flow that GridHeaderCell uses for hover-to-show.
    handleTriggerClick = (event) => {
        event.stopPropagation();
        this.setState((prev) => ({isOpen: !prev.isOpen}));
    };

    handleClickOutside = () => {
        this.setState({isOpen: false});
    };

    stopHeaderEvent = (event) => {
        event.stopPropagation();
    };

    render() {
        const {column, filterOptions} = this.props;
        const {filterComponent: FilterComponent, ...rest} = column;
        return (
            <span style={{display: 'inline'}}
                  onMouseDown={this.stopHeaderEvent}
            >
                <Popover trigger={this.renderIcon()}
                    open={this.state.isOpen}
                    onClick={this.handleTriggerClick}
                    onClickOutside={this.handleClickOutside}
                    placement='bottom-start'
                    appendToBody
                    basic
                    className='filter-popover'
                    hookId="popover-filter"
                >
                    <div>
                        {FilterComponent && <FilterComponent filterOptions={filterOptions} {...rest} />}
                    </div>
                </Popover>
            </span>
        )
    }
}

FilterIcon.propTypes = {
    filterComponent: PropTypes.func,
    column: PropTypes.object,
    filterOptions: PropTypes.array,
    allFilterOptions: PropTypes.object,
    setFilterOptions: PropTypes.func
};

const mapDispatchToProps = ({
    setFilterOptions
});

export default connect(null, mapDispatchToProps)(FilterIcon);
